import type {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import FormPreview, {type FormPreviewInput} from '@/components/FormPreview';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {FormSkeleton} from '@/components/ui/skeleton';
import {graphqlClient} from '@/services/graphqlClient';
import {
  type GetMedicinalProductQuery,
  useGetMedicinalProductQuery,
} from '@/services/graphqlHooks';

export type MedicinalProductDialogProps = DialogProps & {
  productId: string;
};

type MedicinalProduct = GetMedicinalProductQuery['getMedicinalProduct'];

const getMedicinalProductPreviewFields = (
  product: MedicinalProduct,
  t: TFunction
): FormPreviewInput[] =>
  [
    {
      label: t('Product name'),
      value: product.productName,
      key: 'productName',
    },
    {label: t('Common name'), value: product.commonName, key: 'commonName'},
    {
      label: t('Active substance'),
      value: product.activeSubstance,
      key: 'activeSubstance',
    },
    {label: t('Strength'), value: product.strength, key: 'strength'},
    {
      label: t('Pharmaceutical form'),
      value: product.pharmaceuticalForm,
      key: 'pharmaceuticalForm',
    },
    {
      label: t('Route of administration'),
      value: product.routeOfAdministration,
      key: 'routeOfAdministration',
    },
    {label: t('ATC code'), value: product.atcCode, key: 'atcCode'},
    {
      label: t('Marketing authorization holder'),
      value: product.marketingAuthorizationHolder,
      key: 'marketingAuthorizationHolder',
    },
    {
      label: t('Authorization number'),
      value: product.authorizationNumber,
      key: 'authorizationNumber',
    },
    {
      label: t('Authorization validity'),
      value: product.authorizationValidity,
      key: 'authorizationValidity',
    },
    {
      label: t('Procedure type'),
      value: product.procedureType,
      key: 'procedureType',
    },
    {label: t('Legal basis'), value: product.legalBasis, key: 'legalBasis'},
    {
      label: t('Manufacturer name'),
      value: product.manufacturerName,
      key: 'manufacturerName',
    },
    {
      label: t('Manufacturer country'),
      value: product.manufacturerCountry,
      key: 'manufacturerCountry',
    },
    {
      label: t('Importer name'),
      value: product.importerName,
      key: 'importerName',
    },
    {
      label: t('Importer country'),
      value: product.importerCountry,
      key: 'importerCountry',
    },
    {
      label: t('Manufacturer or importer name'),
      value: product.manufacturerOrImporterName,
      key: 'manufacturerOrImporterName',
    },
    {
      label: t('Manufacturer or importer country'),
      value: product.manufacturerOrImporterCountry,
      key: 'manufacturerOrImporterCountry',
    },
    {
      label: t('Export country authorization holder'),
      value: product.exportCountryAuthorizationHolder,
      key: 'exportCountryAuthorizationHolder',
    },
    {
      label: t('Export country'),
      value: product.exportCountry,
      key: 'exportCountry',
    },
    {
      label: t('Previous product name'),
      value: product.previousProductName,
      key: 'previousProductName',
    },
    {
      label: t('Label leaflet'),
      value: product.labelLeaflet,
      key: 'labelLeaflet',
    },
    {
      label: t('Parallel import leaflet'),
      value: product.parallelImportLeaflet,
      key: 'parallelImportLeaflet',
    },
    {
      label: t('Parallel import label leaflet'),
      value: product.parallelImportLabelLeaflet,
      key: 'parallelImportLabelLeaflet',
    },
    {
      label: t('Parallel import packaging markings'),
      value: product.parallelImportPackagingMarkings,
      key: 'parallelImportPackagingMarkings',
    },
    {
      label: t('Educational tools for healthcare professionals'),
      value: product.educationalToolsForHealthcareProfessionals,
      key: 'educationalToolsForHealthcareProfessionals',
    },
    {
      label: t('Educational tools for patients'),
      value: product.educationalToolsForPatients,
      key: 'educationalToolsForPatients',
    },
    {
      label: t('Leaflet'),
      value: product.leafletUrl,
      key: 'leafletUrl',
      isUrl: true,
    },
    {
      label: t('Characteristics'),
      value: product.characteristicsUrl,
      key: 'characteristicsUrl',
      isUrl: true,
    },
  ].filter(field => !!field.value);

const MedicinalProductDialog = ({
  productId,
  onOpenChange,
  ...props
}: MedicinalProductDialogProps) => {
  const {t} = useTranslation();

  const {data, isError, isFetching} = useGetMedicinalProductQuery(
    graphqlClient,
    {productId},
    {enabled: !!productId}
  );

  const product = data?.getMedicinalProduct;
  const showNotFound = !isFetching && (!product || isError);
  const fields = product ? getMedicinalProductPreviewFields(product, t) : [];

  return (
    <Dialog {...props} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {product?.productName ?? t('Medicinal product')}
          </DialogTitle>
        </DialogHeader>
        <DialogSection>
          {isFetching && <FormSkeleton size={8} />}
          {showNotFound && <div>{t('Medicinal product not found')}</div>}
          {!isFetching && product && <FormPreview items={fields} />}
        </DialogSection>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange?.(false)}
          >
            {t('Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MedicinalProductDialog;
